// Crop a picture before uploading it: avatars (a circle), server icons (a square) and banners (wide).
import { useEffect, useRef, useState } from "react";
import "../user-settings.css";

interface Size {
  width: number;
  height: number;
}

/**
 * Pick the part to keep: drag to move, slider (or wheel/pinch-zoom on a trackpad) to zoom. The result is drawn into
 * an `out`-sized canvas and saved as WebP (PNG where the browser can't encode WebP), so uploads are small and never
 * need resizing on the server.
 */
export function ImageCropper({ file, title, view, out: OUT, maxBytes, round = false, onCancel, onDone }: { file: File; title: string; view: Size; out: Size; maxBytes: number; round?: boolean; onCancel: () => void; onDone: (b: Blob) => void }) {
  const VW = view.width;
  const VH = view.height;
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => {
      setImg(i);
      const s = Math.max(VW / i.naturalWidth, VH / i.naturalHeight);
      setPos({ x: (VW - i.naturalWidth * s) / 2, y: (VH - i.naturalHeight * s) / 2 });
    };
    i.onerror = () => setError("That file isn't a picture this browser can open. Try a JPEG or PNG.");
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Esc closes this dialog only (the settings page leaves Esc alone when it's been handled).
  useEffect(() => {
    dialog.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  // Smallest scale that covers the whole frame.
  const base = img ? Math.max(VW / img.naturalWidth, VH / img.naturalHeight) : 1;
  const scale = base * zoom;
  const w = (img?.naturalWidth ?? 0) * scale;
  const h = (img?.naturalHeight ?? 0) * scale;
  /** Keeps the picture covering the whole frame. */
  const clamp = (x: number, y: number, ww = w, hh = h) => ({ x: Math.min(0, Math.max(VW - ww, x)), y: Math.min(0, Math.max(VH - hh, y)) });

  const setZoomAround = (z: number) => {
    if (!img) return;
    const nz = Math.min(4, Math.max(1, z));
    // Zoom about the centre of the frame, so what you're looking at stays put.
    const cx = VW / 2;
    const cy = VH / 2;
    const k = nz / zoom;
    const nw = img.naturalWidth * base * nz;
    const nh = img.naturalHeight * base * nz;
    setPos(clamp(cx - (cx - pos.x) * k, cy - (cy - pos.y) * k, nw, nh));
    setZoom(nz);
  };

  const save = async () => {
    if (!img) return;
    const canvas = document.createElement("canvas");
    canvas.width = OUT.width;
    canvas.height = OUT.height;
    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, -pos.x / scale, -pos.y / scale, VW / scale, VH / scale, 0, 0, OUT.width, OUT.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/webp", 0.86));
    // Browsers that can't make WebP hand back a PNG instead, which the server also takes.
    const out = blob ?? (await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/png")));
    if (!out || out.size > maxBytes) return setError("Couldn't shrink that picture. Try another one.");
    onDone(out);
  };

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className={`modal us-crop${VW > 300 ? " wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby="us-crop-title" tabIndex={-1} ref={dialog}>
        <h2 id="us-crop-title">{title}</h2>
        {error ? (
          <p className="error">{error}</p>
        ) : (
          <>
            <div
              className="us-crop-view"
              style={{ width: VW, height: VH }}
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                drag.current = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y };
              }}
              onPointerMove={(e) => {
                const d = drag.current;
                if (d) setPos(clamp(d.x + e.clientX - d.px, d.y + e.clientY - d.py));
              }}
              onPointerUp={() => (drag.current = null)}
              onPointerCancel={() => (drag.current = null)}
              onWheel={(e) => setZoomAround(zoom * (e.deltaY < 0 ? 1.08 : 1 / 1.08))}
            >
              {img && <img src={img.src} alt="" draggable={false} style={{ width: w, height: h, transform: `translate(${pos.x}px, ${pos.y}px)` }} />}
              <div className={`us-crop-ring${round ? "" : " square"}`} aria-hidden="true" />
            </div>
            <label className="us-zoom">
              <span className="sr-only">Zoom</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M8 11h6" /></svg>
              <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(e) => setZoomAround(Number(e.target.value))} disabled={!img} />
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M8 11h6M11 8v6" /></svg>
            </label>
          </>
        )}
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={!img || !!error}>Save picture</button>
        </div>
      </div>
    </div>
  );
}
